import { FC, useEffect } from 'react';
import { useTranslation } from '@packages/utils';
import useTable from 'components/Base/Table/hooks/useTable';
import { ETableStoragePaths } from 'components/Base/Table/Table.types';
import { Table } from 'components/Base/Table';
import { useGetEventRegistrantsWithChildcareQuery } from 'redux/apiSlice/registrants.slice';
import { TEventRegistrantsWithChildcareUI } from 'data/types/registrants.types';
import { TChildcareListProps } from './ChildcareList.types';
import useGetEventChildcareColumnData from './ChildcareList.data';
import AddChildcareRequest from './AddChildcareRequest';
import ChildcareEditColumn from './ChildcareEditColumn';

const ChildcareList: FC<TChildcareListProps> = ({ eventId }) => {
  const { defaultColumns } = useGetEventChildcareColumnData();
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.details.child-care',
  });

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
  } = useTable<TEventRegistrantsWithChildcareUI>({
    storagePath: `${ETableStoragePaths.EVENT_CHILDCARE}-${eventId}`,
    defaultColumns,
  });

  const { data, isFetching, isLoading, isSuccess } =
    useGetEventRegistrantsWithChildcareQuery(
      {
        page,
        pageSize,
        sortColumn,
        filters: selectedFilters,
        eventId,
      },
      {
        skip: !eventId,
      },
    );

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === 'childcareRequirements') {
          item.render = (_, record) => {
            return (
              <ChildcareEditColumn
                eventId={eventId}
                {...record}
                isReadOnly={data?.allowedOperations.readonly}
              />
            );
          };
        }
        return item;
      }),
    );
  }, [isSuccess]);

  if (!isSuccess) {
    return null;
  }

  return (
    <Table<TEventRegistrantsWithChildcareUI>
      onFiltersReset={onFiltersReset}
      hasFilters={hasFilters}
      currentPage={page}
      columns={columns}
      dataSource={data?.content}
      loading={isFetching && !isLoading}
      pageSize={pageSize}
      tableTitle={t('TITLE')}
      setPageSize={setPageSize}
      totalDataCount={data?.totalElements}
      onChange={handleTableChange}
      rowKey={record => record.registrantId}
      storagePath={ETableStoragePaths.EVENT_CHILDCARE}
      pageHeaderContent={150}
      {...(!data.allowedOperations.readonly && {
        additionalActions: [<AddChildcareRequest eventId={eventId} />],
      })}
    />
  );
};

export default ChildcareList;
